import type { StemRole } from "@repo/audio-engine";

const MIX_PRESETS_STORAGE_KEY = "band-sync-lab:mix-presets:v1";
const MAX_PRESET_NAME_LENGTH = 40;

export interface StemMixSetting {
  readonly volume: number;
  readonly muted: boolean;
  readonly solo: boolean;
}

export interface MixPreset {
  readonly name: string;
  readonly stems: Partial<Record<StemRole, StemMixSetting>>;
}

function normalizeName(rawName: string): string {
  const trimmed = rawName.trim();
  if (!trimmed) {
    return "";
  }
  return trimmed.slice(0, MAX_PRESET_NAME_LENGTH);
}

function normalizeSetting(input: Partial<StemMixSetting> | undefined): StemMixSetting {
  const volumeCandidate =
    typeof input?.volume === "number" && Number.isFinite(input.volume) ? input.volume : 1;
  return {
    volume: Math.max(0, Math.min(1, volumeCandidate)),
    muted: Boolean(input?.muted),
    solo: Boolean(input?.solo),
  };
}

function sanitizePreset(candidate: Partial<MixPreset>): MixPreset | null {
  const name = typeof candidate.name === "string" ? normalizeName(candidate.name) : "";
  if (!name) {
    return null;
  }
  const stems = (candidate.stems ?? {}) as Partial<Record<StemRole, Partial<StemMixSetting>>>;
  const nextStems: Partial<Record<StemRole, StemMixSetting>> = {};
  for (const [role, setting] of Object.entries(stems)) {
    nextStems[role as StemRole] = normalizeSetting(setting);
  }
  return {
    name,
    stems: nextStems,
  };
}

export function loadMixPresets(): MixPreset[] {
  if (typeof window === "undefined") {
    return [];
  }
  const raw = window.localStorage.getItem(MIX_PRESETS_STORAGE_KEY);
  if (!raw) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    const sanitized = parsed
      .map((item) => sanitizePreset((item ?? {}) as Partial<MixPreset>))
      .filter((item): item is MixPreset => item !== null);
    saveMixPresets(sanitized);
    return sanitized;
  } catch {
    return [];
  }
}

export function saveMixPresets(presets: MixPreset[]): void {
  if (typeof window === "undefined") {
    return;
  }
  window.localStorage.setItem(MIX_PRESETS_STORAGE_KEY, JSON.stringify(presets));
}

export function upsertMixPreset(presets: MixPreset[], preset: MixPreset): MixPreset[] {
  const sanitized = sanitizePreset(preset);
  if (!sanitized) {
    return presets;
  }
  const filtered = presets.filter(
    (entry) => entry.name.toLowerCase() !== sanitized.name.toLowerCase(),
  );
  const next = [sanitized, ...filtered].slice(0, 20);
  saveMixPresets(next);
  return next;
}

export function deleteMixPreset(presets: MixPreset[], name: string): MixPreset[] {
  const normalized = normalizeName(name).toLowerCase();
  const next = presets.filter((entry) => entry.name.toLowerCase() !== normalized);
  saveMixPresets(next);
  return next;
}
